import { useMemo } from 'react';

import { CheckoutButtonsProps } from '#components/molecules/Cart/buttons/CheckoutButtons/CheckoutButtons.js';
import { SeasonTicketsSection } from '#components/molecules/SeasonTicketsSection/SeasonTicketsSection.js';
import { TicketsSection } from '#components/molecules/TicketsSection/TicketsSection.js';
import { useInfiniteListEventDates, useListTickets } from '@givebacks/api/store';
import { DateTime } from 'luxon';
import { Modal, ModalProps, NextPageLoader, Skeleton, Stack, Text, dotSeparator } from 'ui';

import { StoreItemModalsProps } from '../StoreItemModals/StoreItemModals';

const GetTicketsModalContent = ({
  eventId,
  causeId,
  modalProps,
  checkoutButtonsProps,
  additionalFieldsProps,
}: {
  eventId?: string;
  causeId?: string;
  modalProps?: Partial<ModalProps>;
  checkoutButtonsProps?: Partial<CheckoutButtonsProps>;
  additionalFieldsProps?: StoreItemModalsProps['additionalFieldsProps'];
}) => {
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteListEventDates({
      eventId,
      causeId,
      queryOptions: {
        options: {
          include: 'event_date_tickets,inventory',
        },
        order: {
          field: 'starting_at',
          direction: 'asc',
        },
      },
    });

  const { data: seasonTicketsData } = useListTickets({
    eventId,
    causeId,
    ticketsType: 'season_passes',
  });

  const hasSeasonTickets = useMemo(() => {
    return seasonTicketsData?.store_items && seasonTicketsData.store_items.length > 0;
  }, [seasonTicketsData?.store_items]);

  const ticketSections = useMemo(() => {
    const eventDates = data?.pages.flatMap((page) => page.event_dates) ?? [];
    if (eventDates.length === 0) return null;

    const sortedEventDates = eventDates
      .sort((firstEventDate, secondEventDate) => {
        const dateA = DateTime.fromISO(firstEventDate.starting_at);
        const dateB = DateTime.fromISO(secondEventDate.starting_at);
        return dateA < dateB ? -1 : dateA > dateB ? 1 : 0;
      })
      .slice();

    const today = DateTime.now().startOf('day');
    const currentAndFutureEvents = sortedEventDates.findIndex((eventDate) => {
      return DateTime.fromISO(eventDate.starting_at).startOf('day') >= today;
    });

    const futureEventDates = sortedEventDates.slice(currentAndFutureEvents);

    const sections = futureEventDates?.map((eventDate) => {
      const date = DateTime.fromISO(eventDate.starting_at).toFormat('ccc, LLL d');
      const time = DateTime.fromISO(eventDate.starting_at ?? '')
        .toLocaleString(DateTime.TIME_SIMPLE)
        .replace(' AM', 'am')
        .replace(' PM', 'pm');
      const timezone = DateTime.fromISO(eventDate.starting_at).toFormat('ZZZZ');

      const ticketSectionTitle = eventDate.title
        ? [eventDate.title, date, time, timezone].join(dotSeparator)
        : [date, time, timezone].join(dotSeparator);

      return (
        <Skeleton visible={isLoading} key={eventDate.uuid}>
          <TicketsSection
            title={
              <Text fz="sm" fw="500">
                {ticketSectionTitle}
              </Text>
            }
            eventId={eventId}
            causeId={causeId}
            eventDateId={eventDate.uuid}
            modalProps={modalProps}
            checkoutButtonsProps={checkoutButtonsProps}
            additionalFieldsProps={additionalFieldsProps}
          />
        </Skeleton>
      );
    });

    return sections;
  }, [
    data?.pages,
    isLoading,
    eventId,
    causeId,
    modalProps,
    checkoutButtonsProps,
    additionalFieldsProps,
  ]);

  return (
    <Stack gap="lg">
      {hasSeasonTickets && (
        <SeasonTicketsSection
          eventId={eventId}
          causeId={causeId}
          title={
            <Text fz="sm" fw={500}>
              Season Tickets
            </Text>
          }
          checkoutButtonsProps={checkoutButtonsProps}
          additionalFieldsProps={additionalFieldsProps}
        />
      )}
      {ticketSections}
      <NextPageLoader
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        handleFetchNextPage={() => void fetchNextPage()}
      />
    </Stack>
  );
};

interface GetTicketsModalProps extends ModalProps {
  eventId?: string;
  causeId?: string;
  checkoutButtonsProps?: Partial<CheckoutButtonsProps>;
  additionalFieldsProps?: StoreItemModalsProps['additionalFieldsProps'];
}

const GetTicketsModal = ({
  eventId,
  causeId,
  opened,
  onClose,
  title,
  checkoutButtonsProps,
  additionalFieldsProps,
  ...props
}: GetTicketsModalProps) => {
  return (
    <Modal title={title ?? 'Select Tickets'} opened={opened} onClose={onClose} {...props}>
      <GetTicketsModalContent
        causeId={causeId}
        eventId={eventId}
        modalProps={props}
        checkoutButtonsProps={checkoutButtonsProps}
        additionalFieldsProps={additionalFieldsProps}
      />
    </Modal>
  );
};

export { GetTicketsModal };
